import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { profileKeys } from "./profile";
import type { Profile } from "./profile";
import { refusalFrom } from "./refusal";

export const NOTIFICATION_PREFERENCES_PATH = "/api/v1/profile/notification-preferences";
/** What a person chose about notification email in this workspace (ADR-0130). */
export type NotificationPreferences = {
  email_notifications: boolean;
  /** One summary per delivery run instead of one email per item. */
  email_digest: boolean;
  /** Set by the server when the provider reported this person's address dead; read-only. */
  email_suppressed_reason: "bounce" | "complaint" | null;
};
/** What may be changed: either choice, or both. Naming neither is a 422. */
export type NotificationPreferenceChange = Partial<
  Pick<NotificationPreferences, "email_notifications" | "email_digest">
>;
export const notificationPreferenceKey = ["profile", "notification-preferences"] as const;

export function readNotificationPreferences(value: unknown): NotificationPreferences {
  if (value === null || typeof value !== "object"
    || Object.keys(value).length !== 3
    || !("email_notifications" in value)
    || typeof value.email_notifications !== "boolean"
    || !("email_digest" in value)
    || typeof value.email_digest !== "boolean"
    || !("email_suppressed_reason" in value)
    || (value.email_suppressed_reason !== null
      && value.email_suppressed_reason !== "bounce"
      && value.email_suppressed_reason !== "complaint")) {
    throw new Error("Your notification preference response could not be read.");
  }
  return {
    email_notifications: value.email_notifications,
    email_digest: value.email_digest,
    email_suppressed_reason: value.email_suppressed_reason,
  };
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationPreferenceKey,
    retry: false,
    queryFn: async (): Promise<NotificationPreferences> => {
      const { data, error, response } = await api.GET(NOTIFICATION_PREFERENCES_PATH, {});
      if (!response.ok) throw refusalFrom(response, error, "Your notification preference could not be read.");
      return readNotificationPreferences(data);
    },
  });
}

export function useSaveNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (change: NotificationPreferenceChange): Promise<NotificationPreferences> => {
      const { data, error, response } = await api.PATCH(NOTIFICATION_PREFERENCES_PATH, {
        body: change,
      });
      if (!response.ok) throw refusalFrom(response, error, "Your notification preference could not be saved.");
      return readNotificationPreferences(data);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(notificationPreferenceKey, saved);
      queryClient.setQueryData<Profile>(profileKeys.profile(), (profile) =>
        profile ? { ...profile, email_notifications: saved.email_notifications } : profile);
    },
  });
}
